import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus } from "lucide-react";

export default function CustomerEditDialog({ open, onOpenChange, client, onSaved }) {
  const [c, setC] = useState({});
  const [subs, setSubs] = useState([]);
  const [nw, setNw] = useState({ av_name: "", license_info: "", amount: "", subscription_start: "", subscription_end: "" });
  const [busy, setBusy] = useState(false);

  // sync when opened
  if (open && c._synced !== client.id) {
    setC({ _synced: client.id, name: client.name || "", phone: client.phone || "", email: client.email || "", company: client.company || "", address: client.address || "", country: client.country || "", customer_id: client.customer_id || "", agent: client.agent || "" });
    setSubs(client.subscriptions || []);
  }

  const saveContact = async () => {
    setBusy(true);
    try {
      const { _synced, ...payload } = c;
      await api.put(`/clients/${client.id}`, payload);
      toast.success("Customer updated"); onSaved && onSaved();
    } catch (e) { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const patchSub = async (sid, field, value) => {
    try { await api.put(`/clients/${client.id}/subscriptions/${sid}`, { [field]: value }); onSaved && onSaved(); }
    catch (e) { toast.error("Update failed"); }
  };
  const delSub = async (sid) => { try { const { data } = await api.delete(`/clients/${client.id}/subscriptions/${sid}`); setSubs(data.subscriptions || []); onSaved && onSaved(); toast.success("Subscription removed"); } catch (e) { toast.error("Delete failed"); } };
  const addSub = async () => {
    if (!nw.av_name && !nw.license_info) { toast.error("Add antivirus or license info"); return; }
    try { const { data } = await api.post(`/clients/${client.id}/subscriptions`, { ...nw, amount: parseFloat(nw.amount) || 0, date: nw.subscription_start || null });
      setSubs(data.subscriptions || []); setNw({ av_name: "", license_info: "", amount: "", subscription_start: "", subscription_end: "" }); onSaved && onSaved(); toast.success("Subscription added");
    } catch (e) { toast.error("Add failed"); }
  };

  const set = (k, v) => setC((x) => ({ ...x, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Edit Customer</DialogTitle><DialogDescription>Update contact details and subscriptions.</DialogDescription></DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3">
          {[["name", "Name"], ["phone", "Phone"], ["email", "Email"], ["company", "Company"], ["customer_id", "Customer ID"], ["country", "Country"], ["agent", "Agent"], ["address", "Address"]].map(([k, lbl]) => (
            <div key={k} className="grid gap-1.5"><Label>{lbl}</Label><Input value={c[k] || ""} onChange={(e) => set(k, e.target.value)} className="rounded-sm" data-testid={`edit-${k}`} /></div>
          ))}
        </div>
        <div className="flex justify-end"><Button className="rounded-sm" onClick={saveContact} disabled={busy} data-testid="save-contact">Save details</Button></div>

        <div className="border-t border-border pt-4">
          <h4 className="font-heading font-semibold mb-2">Subscriptions</h4>
          <div className="space-y-2" data-testid="edit-subs-list">
            {subs.map((s) => (
              <div key={s.id} className="grid grid-cols-12 gap-2 items-center rounded-sm border border-border p-2" data-testid={`edit-sub-${s.id}`}>
                <Input defaultValue={s.av_name} onBlur={(e) => e.target.value !== s.av_name && patchSub(s.id, "av_name", e.target.value)} className="rounded-sm col-span-3 h-8 text-xs" placeholder="Antivirus" />
                <Input defaultValue={s.license_info} onBlur={(e) => e.target.value !== s.license_info && patchSub(s.id, "license_info", e.target.value)} className="rounded-sm col-span-3 h-8 text-xs" placeholder="License" />
                <Input type="number" defaultValue={s.amount} onBlur={(e) => parseFloat(e.target.value) !== s.amount && patchSub(s.id, "amount", parseFloat(e.target.value) || 0)} className="rounded-sm col-span-2 h-8 text-xs font-mono" placeholder="Amt" />
                <Input defaultValue={s.subscription_end || ""} onBlur={(e) => e.target.value !== s.subscription_end && patchSub(s.id, "subscription_end", e.target.value)} className="rounded-sm col-span-2 h-8 text-xs font-mono" placeholder="End YYYY-MM-DD" />
                <button onClick={() => patchSub(s.id, "refunded", !s.refunded)} className="col-span-1" title="Toggle refunded"><Badge variant="outline" className={`rounded-sm text-[10px] ${s.refunded ? "bg-destructive/15 text-destructive border-destructive/30" : ""}`}>{s.refunded ? "Rfnd" : "OK"}</Badge></button>
                <button onClick={() => delSub(s.id)} className="col-span-1 text-muted-foreground hover:text-primary flex justify-center"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-12 gap-2 items-end mt-3">
            <Input value={nw.av_name} onChange={(e) => setNw({ ...nw, av_name: e.target.value })} className="rounded-sm col-span-3 h-8 text-xs" placeholder="Antivirus" data-testid="new-sub-av" />
            <Input value={nw.license_info} onChange={(e) => setNw({ ...nw, license_info: e.target.value })} className="rounded-sm col-span-3 h-8 text-xs" placeholder="License" />
            <Input type="number" value={nw.amount} onChange={(e) => setNw({ ...nw, amount: e.target.value })} className="rounded-sm col-span-2 h-8 text-xs font-mono" placeholder="Amt" />
            <Input value={nw.subscription_start} onChange={(e) => setNw({ ...nw, subscription_start: e.target.value })} className="rounded-sm col-span-3 h-8 text-xs font-mono" placeholder="Start YYYY-MM-DD" />
            <Button size="icon" className="rounded-sm col-span-1 h-8" onClick={addSub} data-testid="add-sub-btn"><Plus size={14} /></Button>
          </div>
        </div>

        <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
